import { useAsyncOperation } from "@core/hooks/use-async-operation";
import { KeyboardKeys, useKeyboardKeyPressed } from "@core/hooks/use-keyboard-key-pressed";
import { useI18n } from "@core/i18n/i18n";
import { getStartedManager, healthcareTeamManager } from "@core/service-container";
import {
	HealthcareTeamInvitationInWrongCountryError,
	InvitationPtAlreadyInHctError,
} from "@domain/healthcare-team/healthcare-team-errors";
import { HealthcareTeamInvitationPatientData } from "@domain/healthcare-team/healthcare-team-manager";
import styled from "@emotion/styled";
import { Button } from "@ui/components/buttons/button";
import { ErrorMessage } from "@ui/components/common/error-message";
import { TextInput } from "@ui/components/inputs/text-input";
import { stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import { isNotNil } from "@utils/assert";
import { motion } from "framer-motion";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";

interface HctInvitationCodeFormProps {
	onConfirm: (patientData: HealthcareTeamInvitationPatientData) => void;
	onBack: () => void;
}

const CODE_PREFIX = "P-";

export const HctInvitationCodeForm: React.FC<HctInvitationCodeFormProps> = ({ onConfirm, onBack }) => {
	const { format } = useI18n();
	const [isHover, setIsHover] = useState(false);
	const [isFocus, setIsFocus] = useState(false);
	const { control, handleSubmit, setError } = useForm({ defaultValues: { code: "" } });

	const [onSubmit, loading] = useAsyncOperation(
		async (data: { code: string }) => {
			try {
				const patientData = await healthcareTeamManager.joinHealthcareTeam(CODE_PREFIX + data.code);
				await getStartedManager.tryMarkPatientsStepAsCompleted();
				onConfirm(patientData);
			} catch (e) {
				let errorMessage = format("home.addPatientDialog.invitationCodeForm.invalidError");
				if (e instanceof HealthcareTeamInvitationInWrongCountryError) {
					errorMessage = format("home.addPatientDialog.invitationCodeForm.wrongCountryError");
				}
				if (e instanceof InvitationPtAlreadyInHctError) {
					errorMessage = format("home.addPatientDialog.invitationCodeForm.alreadyInHctError");
				}
				setError("code", { type: "manual", message: errorMessage });
			}
		},
		{ deps: [format, onConfirm, setError] },
	);

	useKeyboardKeyPressed({ [KeyboardKeys.Enter]: handleSubmit(onSubmit) });

	return (
		<Container onSubmit={handleSubmit(onSubmit)}>
			<FormContainer>
				<p>{format("home.addPatientDialog.invitationCodeForm.title")}</p>
				<Controller
					name="code"
					control={control}
					rules={{
						required: format("home.addPatientDialog.invitationCodeForm.requireError"),
						pattern: {
							value: /^[A-Z0-9]{6}$/,
							message: format("home.addPatientDialog.invitationCodeForm.formatError"),
						},
					}}
					render={({ field, fieldState }) => (
						<WideContainer>
							<InputContainer isHighlighted={isHover || isFocus} isError={isNotNil(fieldState.error?.message)}>
								<span className="p-inputgroup">
									<Prefix className="p-inputgroup-addon" isError={isNotNil(fieldState.error?.message)}>
										{CODE_PREFIX}
									</Prefix>
									<CodeInput
										{...field}
										error={fieldState.invalid}
										placeholder={format("home.addPatientDialog.invitationCodeForm.placeholder")}
										style={{ alignSelf: "stretch", width: "100%" }}
										onMouseEnter={() => setIsHover(true)}
										onMouseLeave={() => setIsHover(false)}
										maxLength={6}
										onFocus={() => setIsFocus(true)}
										onBlur={() => setIsFocus(false)}
									/>
								</span>
							</InputContainer>
							<ErrorMessage>{fieldState.error?.message}</ErrorMessage>
						</WideContainer>
					)}
				/>
			</FormContainer>
			<Footer>
				<Button uiStyle="secondary" label={format("global.dialog.back")} onClick={onBack} />
				<Button label={format("global.dialog.next")} type="submit" loading={loading} />
			</Footer>
		</Container>
	);
};

const Container = styled(motion.form)`
	${stack(16, "spacebetween")};
	flex: 1;
`;

const FormContainer = styled.div`
	${stack(24)};
`;

const Prefix = styled.span<{ isError: boolean }>`
	padding-right: 0;
	color: ${({ isError }) => (isError ? colors.rejection : colors.black)};
	background-color: ${colors.primaryBackground};
`;

const CodeInput = styled(TextInput)`
	padding-left: 0;
	border: 0;
`;

const WideContainer = styled.div`
	${stack(8)}
	align-self: stretch;
	display: flex;
`;

const getInputBorderColor = ({
	isError,
	isHighlighted,
}: {
	isHighlighted: boolean;
	isError: boolean;
}): string => {
	if (isError) {
		return colors.rejection;
	}

	if (isHighlighted) {
		return colors.primary75;
	}

	return colors.white;
};

const InputContainer = styled.div<{ isHighlighted: boolean; isError: boolean }>`
	align-self: stretch;
	display: flex;
	border: 1px solid ${({ isHighlighted, isError }) => getInputBorderColor({ isHighlighted, isError })};
	border-radius: 8px;
`;

const Footer = styled.div`
	display: flex;
	justify-content: space-between;
	align-self: stretch;
`;
